import React, { useCallback, useEffect, useMemo, useState } from "react";
import * as Yup from "yup";
import { Formik, Form } from "formik";

import { Button, ErrorMsg, Flex, Grid, Label, Toastify } from "atoms";
import { FormCheckbox, FormInput, FormSelect } from "molecules/FormInput";
import { optionFetch } from "utils/utilities";
import { usePropsoch } from "contexts/Propsoch";
import { useParams } from "react-router-dom";
import { PLOT } from "utils/constants";

export const SpecificationForm = ({
  onNext,
  onPrevious,
  bindHitOnSave,
  fetchProperty,
  rootProperty,
}) => {
  const {
    actions: {
      getPropertyListingSpecifications,
      createPropertyListingSpecifications,
      updatePropertyListingSpecifications,
    },
  } = usePropsoch();

  const TYPES = useMemo(
    () => [
      ...(!rootProperty?.types?.[0]?.name?.toLowerCase()?.includes(PLOT)
        ? [
            {
              title: "Flooring",
              selects: [
                { title: "Living", label: "living", value: "living" },
                { title: "Bedroom", label: "bedroom", value: "bedroom" },
                {
                  title: "Master Bedroom",
                  label: "masterbedroom",
                  value: "masterbedroom",
                },
                { title: "Kitchen", label: "kitchen", value: "kitchen" },
                { title: "Toilet", label: "toilet", value: "toilet" },
              ],
            },
            {
              title: "Doors & Windows",
              selects: [
                { title: "Main Door", label: "maindoor", value: "maindoor" },
                {
                  title: "Internal Door",
                  label: "internaldoor",
                  value: "internaldoor",
                },
                {
                  title: "Toilet Door",
                  label: "toiletdoor",
                  value: "toiletdoor",
                },
                {
                  title: "Balcony Door",
                  label: "balconydoor",
                  value: "balconydoor",
                },
                { title: "Window", label: "window", value: "window" },
              ],
            },
            {
              title: "Paint",
              selects: [
                {
                  title: "Internal Paint",
                  label: "internalpaint",
                  value: "internalpaint",
                },
                {
                  title: "External Paint",
                  label: "externalpaint",
                  value: "externalpaint",
                },
              ],
            },
            {
              title: "Bathroom",
              selects: [
                {
                  title: "Cp fittings",
                  label: "cpfittings",
                  value: "cpfittings",
                },
                {
                  title: "Sanitaryware",
                  label: "sanitaryware",
                  value: "sanitaryware",
                },
              ],
            },
          ]
        : []),
      {
        title: "Roads",
        selects: [{ title: "Roads", label: "roads", value: "roads" }],
      },
    ],
    [rootProperty]
  );

  const { id } = useParams();

  // fetch property
  const [property, setProperty] = useState(null);

  useEffect(() => {
    id &&
      getPropertyListingSpecifications(id)?.then((data) => {
        setProperty(data);

        const specs = {};
        data?.specificationData
          ?.filter((i) => i?.specification_type !== "additionalfeatures")
          ?.forEach((item) => {
            specs[item?.specification_type] = {
              label: item?.specification_name,
              value: item?.specification_id,
              data: { ...item },
            };
          });
        // reformat type
        data &&
          setProperty((prev) => ({
            ...prev,
            additionalFeatures: data?.specificationData
              ?.filter((i) => i?.specification_type === "additionalfeatures")
              ?.map((item) => item?.specification_id),
            ...specs,
          }));
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // submit
  const [submitLoading, setSubmitLoading] = useState(false);
  const onSubmit = useCallback(
    ({
      constructionTechnology,
      additionalFeatures,
      specificationComments,
      ...values
    }) => {
      setSubmitLoading(true);
      if (values?.dirty) {
        // new nearbyAreas total
        const specificationIds = [...additionalFeatures];

        TYPES?.forEach((type) => {
          type?.selects?.forEach((select) => {
            if (values?.[select?.value]) {
              specificationIds.push(values?.[select?.value]?.value);
            }
          });
        });

        // construction Tech later added via specifications
        constructionTechnology &&
          specificationIds.push(constructionTechnology?.value);

        const formattedValues = {
          propertyId: id,
          // constructionTechnology,
          specificationIds,
          specificationComments,
        };

        if (property) {
          updatePropertyListingSpecifications(
            { ...formattedValues },
            id,
            rootProperty?.name
          )
            .then(() => {
              Toastify("success", "Specifications updated");
              fetchProperty()?.then(() => {
                onNext();
              });
            })
            .catch((e) => {
              Toastify("error", "Specifications not updated");
            })
            .finally(() => {
              setSubmitLoading(false);
            });
        } else {
          createPropertyListingSpecifications(
            formattedValues,
            rootProperty?.name
          )
            .then(() => {
              Toastify("success", "Specifications created");
              fetchProperty()?.then(() => {
                onNext();
              });
            })
            .catch((e) => {
              Toastify("error", "Specifications not created");
            })
            .finally(() => {
              setSubmitLoading(false);
            });
        }
      } else {
        if (id) {
          onNext();
        }
      }
    },
    [
      createPropertyListingSpecifications,
      id,
      updatePropertyListingSpecifications,
      onNext,
      property,
      rootProperty,
      fetchProperty,
      TYPES,
    ]
  );

  // additional features
  const [additionalFeatures, setAdditionalFeatures] = useState([]);

  useEffect(() => {
    // additional features
    optionFetch(
      `/specifications/get/type?type=additionalfeatures`,
      "name",
      "id"
    ).then((data) => {
      setAdditionalFeatures(data);
    });
  }, []);

  const validationSchema = Yup.object().shape({
    ...(!rootProperty?.types?.[0]?.name?.toLowerCase()?.includes(PLOT) && {
      constructionTechnology: Yup.object().shape({
        label: Yup.string()
          .typeError(`Construction technology is required`)
          .required(`Construction technology is required`),
        value: Yup.string()
          .typeError(`Construction technology is required`)
          .required(`Construction technology is required`),
      }),
    }),

    ...TYPES?.map((type) =>
      type?.selects
        ?.map((selector) => ({
          [selector?.value]: Yup.object().shape({
            label: Yup.string()
              .typeError(`${selector?.title} is required`)
              .required(`${selector?.title} is required`),
            value: Yup.string()
              .typeError(`${selector?.title} is required`)
              .required(`${selector?.title} is required`),
          }),
        }))
        ?.reduce((acc, cur) => ({ ...acc, ...cur }), {})
    )?.reduce((acc, cur) => ({ ...acc, ...cur }), {}),

    additionalFeatures: Yup.array()
      .min(1, "Choose a feature")
      .typeError("Features is required")
      .required("Features is required"),
    specificationComments: Yup.string()
      .nullable()
      .when("additionalFeatures", {
        is: (v) =>
          [...v]?.filter((i) =>
            additionalFeatures
              ?.find((t) => t?.value === i)
              ?.label?.toLowerCase()
              ?.includes("other")
          )?.length > 0,
        then: Yup.string().required("Add comments"),
      }),
  });

  return (
    <Formik
      initialValues={
        property
          ? property
          : {
              ...(!rootProperty?.types?.[0]?.name
                ?.toLowerCase()
                ?.includes(PLOT) && { constructionTechnology: "" }),
              additionalFeatures: [],
              specificationComments: "",
              ...TYPES?.map((type) =>
                type?.selects
                  ?.map((selector) => ({ [selector?.value]: "" }))
                  ?.reduce((acc, cur) => ({ ...acc, ...cur }), {})
              )?.reduce((acc, cur) => ({ ...acc, ...cur }), {}),
            }
      }
      onSubmit={onSubmit}
      validationSchema={validationSchema}
      enableReinitialize
    >
      {({ values, submitForm, dirty, setFieldValue, touched, errors }) => {
        bindHitOnSave(() => {
          setFieldValue("dirty", dirty);
          submitForm();
        });
        return (
          <Form>
            <Grid gridGap="3.2rem" mt="2.4rem">
              {!rootProperty?.types?.[0]?.name
                ?.toLowerCase()
                ?.includes(PLOT) && (
                <Grid gridTemplateColumns="1fr 1fr" gridGap="2.4rem" mt="1rem">
                  <Grid gridRowGap="1.6rem">
                    <Label text="Construction Technology" req />
                    {/* <FormInput
                    name="constructionTechnology"
                    type="text"
                    placeholder="Enter Text"
                    req
                  /> */}
                    <FormSelect
                      name="constructionTechnology"
                      placeholder="Select drop down"
                      // options={[]}
                      loadOptions={(inputString) =>
                        optionFetch(
                          `/specifications/get/type?type=constructionTechnology`,
                          "name",
                          "id",
                          inputString
                        )
                      }
                      isOptionLoadAsync
                      defaultOptions
                    />
                  </Grid>
                </Grid>
              )}
              {TYPES?.map((type, index) => (
                <Grid key={index} gridGap="2.4rem">
                  <Label
                    fontSize="1.8rem"
                    color="secondary.700"
                    astrixColor="secondary.700"
                    text={type?.title}
                  />
                  <Grid gridTemplateColumns="1fr 1fr" gridGap="2.4rem">
                    {type?.selects?.map((selector, selIndex) => (
                      <Grid gridRowGap="1.6rem" key={selIndex}>
                        <Label text={selector?.title} req />
                        <FormSelect
                          name={selector?.value}
                          placeholder="Select drop down"
                          // options={[]}
                          loadOptions={(inputString) =>
                            optionFetch(
                              `/specifications/get/type?type=${selector?.value}`,
                              "name",
                              "id",
                              inputString
                            )
                          }
                          isOptionLoadAsync
                          defaultOptions
                        />
                      </Grid>
                    ))}
                  </Grid>
                </Grid>
              ))}

              <Grid gridGap="2.4rem" pr="20rem">
                <Label
                  fontSize="1.8rem"
                  color="secondary.700"
                  astrixColor="secondary.700"
                  text="Additional Features"
                  req
                />
                <Flex gridGap="3rem" flexWrap="wrap">
                  {additionalFeatures?.map((item, index) => (
                    <FormCheckbox
                      key={index}
                      name="additionalFeatures"
                      height="1.8rem"
                      label={item?.label}
                      value={item?.value}
                    />
                  ))}
                </Flex>
                {touched?.additionalFeatures && errors?.additionalFeatures && (
                  <ErrorMsg text={errors?.additionalFeatures} />
                )}
              </Grid>
              {[...(values?.additionalFeatures || [])]?.filter((i) =>
                additionalFeatures
                  ?.find((t) => t?.value === i)
                  ?.label?.toLowerCase()
                  ?.includes("other")
              )?.length > 0 && (
                <Grid gridTemplateColumns="1fr 1fr" gridGap="2.4rem">
                  <Grid gridRowGap="1.6rem">
                    <Label text="Comments" req />
                    <FormInput
                      name="specificationComments"
                      type="text"
                      placeholder="Enter Comments"
                      req
                    />
                  </Grid>
                </Grid>
              )}
              <Flex my="2rem" gridGap="1.6rem">
                <Button
                  px="3rem"
                  width="fit-content"
                  variant="primaryOutline"
                  height="4.8rem"
                  onClick={onPrevious}
                >
                  Previous
                </Button>
                <Button
                  px="3rem"
                  width="fit-content"
                  variant="primary"
                  height="4.8rem"
                  type="submit"
                  loading={submitLoading}
                  onClick={() => {
                    setFieldValue("dirty", dirty);
                    submitForm();
                  }}
                >
                  Next
                </Button>
              </Flex>
            </Grid>
          </Form>
        );
      }}
    </Formik>
  );
};
